import { useLayoutEffect, useState } from "react";

export type InboxShellPanelId = "book" | "content" | "mini-app" | "chat" | "chat-mount";
export type InboxSidePanelId = "book" | "mini-app" | "chat";
export type InboxSidePanelWidths = Partial<Record<InboxSidePanelId, number>>;
export type InboxPanelLayout<T extends string> = Record<T, number>;

export const INBOX_SIDE_PANEL_WIDTHS_STORAGE_KEY = "inbox-side-panel-widths";
// A row spends roughly 120px on the leading gutter and the trailing date, so
// this leaves ~280px of text. Below the row's own `@3xl` breakpoint the sender
// stacks above the subject and both truncate, so anything narrower reads as two
// ellipses rather than a scannable list.
export const INBOX_PANEL_MIN_WIDTH_PX = 400;
// The hard floor the conversation may be dragged to, not the width it reads well at.
export const CONVERSATION_PANEL_MIN_WIDTH_PX = 400;
// Clears a typical 600-700px email body plus the header, participants row,
// attachment chips and composer that frame it.
export const CONVERSATION_COMFORTABLE_WIDTH_PX = 880;
// The inbox+conversation width at which the list earns its column; below it the
// list collapses and the message takes the whole content pane. Shared by the
// auto-collapse policy and the "show list beside message" check so the action is
// offered exactly when taking it produces a usable layout.
export const INBOX_LIST_BESIDE_MESSAGE_MIN_PX = INBOX_PANEL_MIN_WIDTH_PX + CONVERSATION_COMFORTABLE_WIDTH_PX;
// Panel widths reach these thresholds by a percentage round-trip, and the shell
// fits panels to land exactly on them, so a measured width can sit a sliver
// under a target it is meant to satisfy. Every width comparison against a
// threshold allows this much slack, otherwise restoring the list to exactly its
// minimum can immediately collapse it again.
export const LAYOUT_WIDTH_TOLERANCE_PX = 0.5;

const SHELL_SEPARATOR_WIDTH_PX = 1;
const INBOX_SIDE_PANEL_IDS = ["book", "mini-app", "chat"] as const satisfies readonly InboxSidePanelId[];

export const INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX: Record<InboxSidePanelId, number> = {
  book: 280,
  chat: 360,
  "mini-app": 320,
};

export const INBOX_SIDE_PANEL_MIN_WIDTHS_PX: Record<InboxSidePanelId, number> = {
  book: 220,
  chat: 320,
  "mini-app": 280,
};

// The Book and the mini-app are fixed-shape rails: past their max they are a
// narrow column of content in a wide empty panel. Chat is the opposite — the
// transcript, tool output and tables Alby returns all keep using the room — so
// its ceiling is set by how much the broker can spare, not by the surface. The
// shell already refuses to shrink the content below its own minimum, so this
// only has to stop being the limit that binds first on a large display.
export const INBOX_SIDE_PANEL_MAX_WIDTHS_PX: Record<InboxSidePanelId, number> = {
  book: 480,
  chat: 1200,
  "mini-app": 720,
};

// Merged mode is a rendering swap: the `book` panel hosts the insured mini-app
// instead of the Book, so it has to reach the width that surface was built for.
// At 480 a carrier name, a state and a three-dot menu do not fit on one line.
const INBOX_MERGED_RAIL_MAX_WIDTHS_PX: Record<InboxSidePanelId, number> = {
  ...INBOX_SIDE_PANEL_MAX_WIDTHS_PX,
  book: INBOX_SIDE_PANEL_MAX_WIDTHS_PX["mini-app"],
};

export const getInboxSidePanelMaxWidthsPx = (isMergedRail: boolean) =>
  isMergedRail ? INBOX_MERGED_RAIL_MAX_WIDTHS_PX : INBOX_SIDE_PANEL_MAX_WIDTHS_PX;

export const isInboxSidePanelId = (panelId: InboxShellPanelId): panelId is InboxSidePanelId =>
  panelId === "book" || panelId === "mini-app" || panelId === "chat";

export const getPercentLayout = <T extends string>(
  panelIds: T[],
  defaultSizes: Record<T, number>
): InboxPanelLayout<T> => {
  const totalSize = panelIds.reduce((total, panelId) => total + defaultSizes[panelId], 0);

  return Object.fromEntries(
    panelIds.map((panelId) => [panelId, totalSize > 0 ? (defaultSizes[panelId] / totalSize) * 100 : 0])
  ) as InboxPanelLayout<T>;
};

const getVisibleShellSeparatorCount = (panelIds: InboxShellPanelId[]) =>
  Math.max(0, panelIds.filter((panelId) => panelId !== "chat-mount").length - 1);

const getResizableShellWidthPx = (containerWidthPx: number, panelIds: InboxShellPanelId[]) =>
  Math.max(0, containerWidthPx - getVisibleShellSeparatorCount(panelIds) * SHELL_SEPARATOR_WIDTH_PX);

const clampSidePanelWidth = (
  panelId: InboxSidePanelId,
  widthPx: number,
  maxWidthsPx: Record<InboxSidePanelId, number>
) => Math.max(INBOX_SIDE_PANEL_MIN_WIDTHS_PX[panelId], Math.min(widthPx, maxWidthsPx[panelId]));

/**
 * A stored width outlives the rail mode that produced it, so storage keeps it
 * under the widest max any mode allows and each layout re-clamps to the maxes it
 * was handed. Dragging the merged rail to 700 and turning merged mode off shows
 * a 480 Book without forgetting the 700 the rail goes back to.
 */
const clampStoredSidePanelWidth = (panelId: InboxSidePanelId, widthPx: number) =>
  clampSidePanelWidth(panelId, widthPx, INBOX_MERGED_RAIL_MAX_WIDTHS_PX);

const fitSidePanelWidthsToContent = (
  sidePanelWidths: InboxSidePanelWidths,
  availableWidthPx: number,
  contentMinWidthPx: number
) => {
  const fittedWidths = { ...sidePanelWidths };
  const totalSideWidthPx = INBOX_SIDE_PANEL_IDS.reduce((total, panelId) => total + (fittedWidths[panelId] ?? 0), 0);
  const maxSideWidthPx = Math.max(0, availableWidthPx - contentMinWidthPx);

  if (totalSideWidthPx <= maxSideWidthPx) {
    return fittedWidths;
  }

  const shrinkableWidthPx = INBOX_SIDE_PANEL_IDS.reduce((total, panelId) => {
    const width = fittedWidths[panelId];

    return width === undefined ? total : total + Math.max(0, width - INBOX_SIDE_PANEL_MIN_WIDTHS_PX[panelId]);
  }, 0);

  if (shrinkableWidthPx <= 0) {
    return fittedWidths;
  }

  const shrinkByPx = Math.min(totalSideWidthPx - maxSideWidthPx, shrinkableWidthPx);

  for (const panelId of INBOX_SIDE_PANEL_IDS) {
    const width = fittedWidths[panelId];

    if (width === undefined) {
      continue;
    }

    const shrinkablePanelWidthPx = Math.max(0, width - INBOX_SIDE_PANEL_MIN_WIDTHS_PX[panelId]);
    fittedWidths[panelId] = width - (shrinkablePanelWidthPx / shrinkableWidthPx) * shrinkByPx;
  }

  return fittedWidths;
};

const buildOrderedLayout = <T extends string>(panelIds: T[], sizes: InboxPanelLayout<T>): InboxPanelLayout<T> =>
  Object.fromEntries(panelIds.map((panelId) => [panelId, sizes[panelId]])) as InboxPanelLayout<T>;

interface BuildInboxShellLayoutOptions {
  panelIds: InboxShellPanelId[];
  sidePanelWidths: InboxSidePanelWidths;
  containerWidthPx: number;
  contentMinWidthPx: number;
  sidePanelMaxWidthsPx: Record<InboxSidePanelId, number>;
}

export const buildInboxShellLayout = ({
  panelIds,
  sidePanelWidths,
  containerWidthPx,
  contentMinWidthPx,
  sidePanelMaxWidthsPx,
}: BuildInboxShellLayoutOptions): InboxPanelLayout<InboxShellPanelId> => {
  const availableWidthPx = getResizableShellWidthPx(containerWidthPx, panelIds);

  if (availableWidthPx <= 0) {
    return getPercentLayout(panelIds, {
      book: INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.book,
      chat: INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX.chat,
      "chat-mount": 0,
      content: contentMinWidthPx,
      "mini-app": INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX["mini-app"],
    });
  }

  const activeSidePanelWidths = panelIds.reduce<InboxSidePanelWidths>((widths, panelId) => {
    if (isInboxSidePanelId(panelId)) {
      widths[panelId] = clampSidePanelWidth(
        panelId,
        sidePanelWidths[panelId] ?? INBOX_SIDE_PANEL_DEFAULT_WIDTHS_PX[panelId],
        sidePanelMaxWidthsPx
      );
    }

    return widths;
  }, {});
  const fittedSidePanelWidths = fitSidePanelWidthsToContent(activeSidePanelWidths, availableWidthPx, contentMinWidthPx);
  const unorderedLayout = panelIds.reduce<InboxPanelLayout<InboxShellPanelId>>(
    (nextLayout, panelId) => {
      if (panelId === "content") {
        return nextLayout;
      }

      const panelWidthPx = panelId === "chat-mount" ? 0 : (fittedSidePanelWidths[panelId] ?? 0);
      nextLayout[panelId] = (panelWidthPx / availableWidthPx) * 100;

      return nextLayout;
    },
    {} as InboxPanelLayout<InboxShellPanelId>
  );
  const sidePanelPercent = panelIds.reduce(
    (total, panelId) => total + (panelId === "content" ? 0 : unorderedLayout[panelId]),
    0
  );

  if (sidePanelPercent > 100) {
    const sidePanelScale = 100 / sidePanelPercent;

    for (const panelId of panelIds) {
      if (panelId !== "content") {
        unorderedLayout[panelId] *= sidePanelScale;
      }
    }

    unorderedLayout.content = 0;
  } else {
    unorderedLayout.content = 100 - sidePanelPercent;
  }

  return buildOrderedLayout(panelIds, unorderedLayout);
};

export const getInboxShellSidePanelWidthsFromLayout = ({
  panelIds,
  layout,
  containerWidthPx,
}: {
  panelIds: InboxShellPanelId[];
  layout: Record<string, number>;
  containerWidthPx: number;
}): InboxSidePanelWidths => {
  const availableWidthPx = getResizableShellWidthPx(containerWidthPx, panelIds);

  if (availableWidthPx <= 0) {
    return {};
  }

  return panelIds.reduce<InboxSidePanelWidths>((widths, panelId) => {
    if (isInboxSidePanelId(panelId)) {
      const percent = layout[panelId];

      if (Number.isFinite(percent) && percent > 0) {
        widths[panelId] = (percent / 100) * availableWidthPx;
      }
    }

    return widths;
  }, {});
};

export const getInboxShellPanelWidthFromLayout = ({
  panelIds,
  layout,
  panelId,
  containerWidthPx,
}: {
  panelIds: InboxShellPanelId[];
  layout: Record<string, number>;
  panelId: InboxShellPanelId;
  containerWidthPx: number;
}) => {
  const availableWidthPx = getResizableShellWidthPx(containerWidthPx, panelIds);
  const percent = layout[panelId];

  if (availableWidthPx <= 0 || typeof percent !== "number" || !Number.isFinite(percent)) {
    return 0;
  }

  return (percent / 100) * availableWidthPx;
};

export const sanitizeInboxSidePanelWidths = (value: unknown): InboxSidePanelWidths => {
  const widths: InboxSidePanelWidths = {};

  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return widths;
  }

  const maybeWidths = value as Record<string, unknown>;

  for (const panelId of INBOX_SIDE_PANEL_IDS) {
    const width = maybeWidths[panelId];

    if (typeof width === "number" && Number.isFinite(width) && width > 0) {
      widths[panelId] = clampStoredSidePanelWidth(panelId, width);
    }
  }

  return widths;
};

export const readInboxSidePanelWidths = (): InboxSidePanelWidths => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const rawWidths = window.localStorage.getItem(INBOX_SIDE_PANEL_WIDTHS_STORAGE_KEY);

    return rawWidths ? sanitizeInboxSidePanelWidths(JSON.parse(rawWidths)) : {};
  } catch {
    return {};
  }
};

export const writeInboxSidePanelWidths = (widths: InboxSidePanelWidths) => {
  try {
    window.localStorage.setItem(INBOX_SIDE_PANEL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    return true;
  } catch {
    return false;
  }
};

/**
 * A layout change reports every panel, and the shell renders stored widths
 * through a clamp and a fit-to-content pass. A reported width matching a layout
 * the shell built is that pass echoing back, not a width anyone chose, so it is
 * not persisted. A drag that lands within the tolerance of the rendered width
 * is indistinguishable from that echo and is dropped too.
 */
export const mergeInboxSidePanelWidths = (
  currentWidths: InboxSidePanelWidths,
  nextWidths: InboxSidePanelWidths,
  builtWidths: InboxSidePanelWidths[]
): InboxSidePanelWidths => {
  let changed = false;
  const mergedWidths = { ...currentWidths };

  for (const panelId of INBOX_SIDE_PANEL_IDS) {
    const nextWidth = nextWidths[panelId];

    if (nextWidth === undefined) {
      continue;
    }

    const isBuiltWidth = builtWidths.some((widths) => {
      const builtWidth = widths[panelId];

      return builtWidth !== undefined && Math.abs(nextWidth - builtWidth) <= LAYOUT_WIDTH_TOLERANCE_PX;
    });

    if (isBuiltWidth) {
      continue;
    }

    const roundedNextWidth = Math.round(clampStoredSidePanelWidth(panelId, nextWidth));
    if (mergedWidths[panelId] !== roundedNextWidth) {
      mergedWidths[panelId] = roundedNextWidth;
      changed = true;
    }
  }

  return changed ? mergedWidths : currentWidths;
};

export const areLayoutsEqual = (currentLayout: Record<string, number>, nextLayout: Record<string, number>) =>
  Object.entries(nextLayout).every(([panelId, nextSize]) => Math.abs((currentLayout[panelId] ?? 0) - nextSize) < 0.1);

export const hasLayoutForPanelIds = (layout: Record<string, number>, panelIds: InboxShellPanelId[]) =>
  Object.keys(layout).length === panelIds.length &&
  panelIds.every((panelId) => typeof layout[panelId] === "number" && Number.isFinite(layout[panelId]));

export const getContentMinWidthPx = (hasConversationPanel: boolean) => {
  if (!hasConversationPanel) {
    return INBOX_PANEL_MIN_WIDTH_PX;
  }

  // The inbox list is collapsible, so the outer shell only reserves the
  // conversation minimum. The nested inbox/conversation group enforces the
  // inbox minimum while it is visible, and auto-collapses it when side panels
  // need the space.
  return CONVERSATION_PANEL_MIN_WIDTH_PX;
};

export const getContainerWidth = (element: HTMLElement | null) => element?.getBoundingClientRect().width ?? 0;

export const useElementWidth = (element: HTMLElement | null) => {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!element) {
      setWidth(0);
      return;
    }

    const updateWidth = () => setWidth(getContainerWidth(element));

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return width;
};
